<h2 mat-dialog-title>{{ isEditMode ? '組織の編集' : '新規組織の追加' }}</h2>
<mat-dialog-content>
  <form [formGroup]="form" class="flex flex-col gap-4 pt-4">
    <mat-form-field appearance="outline" class="w-full">
      <mat-label>組織名</mat-label>
      <input matInput formControlName="name" placeholder="例: 株式会社テスト" required>
      @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
        <mat-error>組織名は必須です</mat-error>
      }
    </mat-form-field>

    <div class="flex flex-col gap-2">
      <p class="font-semibold text-sm">有効機能 (Feature Flags)</p>
      @for (flag of featureFlags; track flag.id) {
        <mat-checkbox [formControlName]="flag.id">{{ flag.name }}</mat-checkbox>
      }
    </div>

    <div class="flex flex-col gap-3 border-t border-outline-variant pt-4" formGroupName="wbgtThresholds">
      <div>
        <p class="font-semibold text-sm m-0">WBGT閾値設定</p>
        <p class="text-xs text-on-surface-variant m-0">未入力の場合はデフォルト値を使用します</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>ほぼ安全 開始値</mat-label>
          <input matInput type="number" formControlName="almostSafe" [placeholder]="defaultWbgtThresholds.almostSafe.toString()">
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>注意 開始値</mat-label>
          <input matInput type="number" formControlName="caution" [placeholder]="defaultWbgtThresholds.caution.toString()">
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>警戒 開始値</mat-label>
          <input matInput type="number" formControlName="warning" [placeholder]="defaultWbgtThresholds.warning.toString()">
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>厳重警戒 開始値</mat-label>
          <input matInput type="number" formControlName="severeWarning" [placeholder]="defaultWbgtThresholds.severeWarning.toString()">
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>危険 開始値</mat-label>
          <input matInput type="number" formControlName="danger" [placeholder]="defaultWbgtThresholds.danger.toString()">
        </mat-form-field>
      </div>

      @if (form.hasError('wbgtThresholdOrder')) {
        <p class="text-sm text-error m-0">閾値は ほぼ安全 &lt; 注意 &lt; 警戒 &lt; 厳重警戒 &lt; 危険 の順に入力してください</p>
      }
    </div>

    <div class="flex flex-col gap-3 border-t border-outline-variant pt-4" formGroupName="temperatureThresholds">
      <div>
        <p class="font-semibold text-sm m-0">温度閾値設定</p>
        <p class="text-xs text-on-surface-variant m-0">未入力の場合はデフォルト値を使用します</p>
      </div>

      <div>
        <p class="text-xs font-medium text-on-surface-variant m-0 mb-2">要注意</p>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>-5℃以下 基準値</mat-label>
            <input matInput type="number" formControlName="freezingDanger" [placeholder]="defaultTemperatureThresholds.freezingDanger.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>0℃以下 基準値</mat-label>
            <input matInput type="number" formControlName="freezingCaution" [placeholder]="defaultTemperatureThresholds.freezingCaution.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>5℃以下 基準値</mat-label>
            <input matInput type="number" formControlName="coldCaution" [placeholder]="defaultTemperatureThresholds.coldCaution.toString()">
          </mat-form-field>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium text-on-surface-variant m-0 mb-2">その他</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>5℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="mild" [placeholder]="defaultTemperatureThresholds.mild.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>10℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="tenOrMore" [placeholder]="defaultTemperatureThresholds.tenOrMore.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>15℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="fifteenOrMore" [placeholder]="defaultTemperatureThresholds.fifteenOrMore.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>20℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="twentyOrMore" [placeholder]="defaultTemperatureThresholds.twentyOrMore.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>25℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="twentyFiveOrMore" [placeholder]="defaultTemperatureThresholds.twentyFiveOrMore.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>30℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="thirtyOrMore" [placeholder]="defaultTemperatureThresholds.thirtyOrMore.toString()">
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>35℃以上 基準値</mat-label>
            <input matInput type="number" formControlName="thirtyFiveOrMore" [placeholder]="defaultTemperatureThresholds.thirtyFiveOrMore.toString()">
          </mat-form-field>
        </div>
      </div>

      @if (form.hasError('temperatureThresholdOrder')) {
        <p class="text-sm text-error m-0">温度閾値は 要注意側とその他側それぞれで小さい値から大きい値の順に入力してください</p>
      }
    </div>
  </form>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button matButton (click)="onCancel()">キャンセル</button>
  <button matButton="tonal" color="primary" [disabled]="form.invalid || isLoading()" (click)="onSubmit()">
    保存
  </button>
</mat-dialog-actions>
